import { API_CONFIG, buildApiUrl, getAuthHeaders } from '../config/api.ts';
import { apiRequest } from '../utils/apiRequest.ts';

// Allowed values (kept in sync with the API's create-inventory-item DTO)
export const INVENTORY_CATEGORIES = ['LIQUOR', 'WINE', 'BOTTLED_BEER', 'DRAFT_BEER', 'MIXER'];
export const INVENTORY_UOMS = ['Bottle', 'Case', 'Keg', 'oz'];
export const ALCOHOL_TYPES = ['Spirit', 'Wine', 'Beer'];

// Types for Inventory Items API
export interface InventoryItem {
  id: number;
  sku: string;
  barcode: string | null;
  itemName: string;
  category: string;
  subCategory: string | null;
  brand: string | null;
  size: string | null;
  uom: string | null;
  alcoholType: string | null;
  activeFlag: boolean;
  userId: number;
  createdDate: string | number;
}

export interface CreateInventoryItemRequest {
  sku: string;
  barcode?: string;
  itemName: string;
  category: string;
  subCategory?: string;
  brand?: string;
  size?: string;
  uom?: string;
  alcoholType?: string;
  activeFlag?: boolean;
}

// Data returned by the external UPC/barcode lookup, used to pre-fill the create form
export interface UpcLookupResult {
  sku: string;
  barcode?: string;
  itemName: string;
  category: string;
  subCategory?: string;
  brand?: string;
  size?: string;
  uom?: string;
  alcoholType?: string;
}

export interface PaginatedInventoryItemsResponse {
  data: InventoryItem[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface InventoryItemsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  upc?: string;
  category?: string;
  activeFlag?: boolean;
}

export const inventoryItemsApi = {
  // Get all inventory items with pagination
  getItems: async (
    params: InventoryItemsQueryParams = {},
    token: string
  ): Promise<PaginatedInventoryItemsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.upc) searchParams.append('upc', params.upc);
    if (params.category) searchParams.append('category', params.category);
    if (params.activeFlag !== undefined) searchParams.append('activeFlag', String(params.activeFlag));

    const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.INVENTORY_ITEMS.BASE)}?${searchParams.toString()}`;

    return apiRequest<PaginatedInventoryItemsResponse>(url, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
  },

  // Create new inventory item
  createItem: async (data: CreateInventoryItemRequest, token: string): Promise<InventoryItem> => {
    return apiRequest<InventoryItem>(
      buildApiUrl(API_CONFIG.ENDPOINTS.INVENTORY_ITEMS.BASE),
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  // Update inventory item (admin only)
  updateItem: async (
    id: number,
    data: Partial<CreateInventoryItemRequest>,
    token: string
  ): Promise<InventoryItem> => {
    return apiRequest<InventoryItem>(
      buildApiUrl(API_CONFIG.ENDPOINTS.INVENTORY_ITEMS.BY_ID(id)),
      {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  // Delete inventory item (admin only)
  deleteItem: async (id: number, token: string): Promise<void> => {
    return apiRequest<void>(
      buildApiUrl(API_CONFIG.ENDPOINTS.INVENTORY_ITEMS.BY_ID(id)),
      {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      }
    );
  },

  // Check whether a scanned UPC already exists in inventory_items (exact match on
  // barcode or SKU, done server-side via the `upc` filter). Checked BEFORE the
  // external lookup so trial API calls are not wasted on items already in the table.
  upcExists: async (upc: string, token: string): Promise<boolean> => {
    const searchParams = new URLSearchParams({ upc, limit: '1' });
    const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.INVENTORY_ITEMS.BASE)}?${searchParams.toString()}`;
    const response = await apiRequest<PaginatedInventoryItemsResponse>(url, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    return response.total > 0;
  },

  // Look up item data for a scanned UPC/barcode to pre-fill the create form.
  // Goes through our API, which proxies the UPCitemdb trial API (the trial API blocks
  // browser CORS). The trial API is rate-limited; a 429 carries the reset time.
  lookupUpc: async (upc: string, token: string): Promise<UpcLookupResult | null> => {
    const response = await fetch(
      buildApiUrl(API_CONFIG.ENDPOINTS.INVENTORY_ITEMS.LOOKUP(upc.trim())),
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );

    const data = await response.json().catch(() => null);

    if (response.status === 429) {
      let message = data?.message || 'UPC lookup limit reached (trial API).';
      if (data?.resetAt) {
        message += ` The limit resets at ${new Date(data.resetAt * 1000).toLocaleString()}.`;
      } else {
        message += ' Please try again later.';
      }
      throw new Error(message);
    }

    if (!response.ok) {
      throw new Error(data?.message || `UPC lookup failed (HTTP ${response.status})`);
    }

    if (data?.code !== 'OK' || !Array.isArray(data.items) || data.items.length === 0) {
      return null;
    }

    return mapUpcItemToLookupResult(upc.trim(), data.items[0]);
  },
};

// Map UPCitemdb's category path (e.g. "Food, Beverages & Tobacco > Beverages >
// Alcoholic Beverages > Liquor & Spirits > Vodka") onto our category / alcohol type
const categorizeUpcItem = (categoryPath: string, title: string) => {
  const haystack = `${categoryPath} ${title}`.toLowerCase();

  const isSpirit = /liquor|spirit|vodka|whisk|bourbon|rum|gin|tequila|brandy|cognac|scotch/.test(haystack);
  const isWine = /wine|champagne|prosecco|sangria|vermouth/.test(haystack);
  const isBeer = /beer|lager|ipa|ale|stout|pilsner|cider/.test(haystack);
  const isKeg = /keg|1\/2 bbl|1\/4 bbl|barrel/.test(haystack);

  let category = 'MIXER';
  let alcoholType: string | undefined;
  if (isSpirit) {
    category = 'LIQUOR';
    alcoholType = 'Spirit';
  } else if (isWine) {
    category = 'WINE';
    alcoholType = 'Wine';
  } else if (isBeer) {
    category = isKeg ? 'DRAFT_BEER' : 'BOTTLED_BEER';
    alcoholType = 'Beer';
  }

  // Last segment of the category path makes a reasonable sub-category (e.g. "Vodka")
  const segments = categoryPath.split('>').map((s) => s.trim()).filter(Boolean);
  const subCategory = segments.length > 0 ? segments[segments.length - 1] : undefined;

  return { category, alcoholType, subCategory };
};

const CATEGORY_SKU_PREFIX: Record<string, string> = {
  LIQUOR: 'LQ',
  WINE: 'WN',
  BOTTLED_BEER: 'BB',
  DRAFT_BEER: 'DB',
  MIXER: 'MX',
};

// Generate an internal SKU like "LQ-TITOS-1L" from the looked-up item data
const generateSku = (category: string, brand: string, size: string, title: string): string => {
  const prefix = CATEGORY_SKU_PREFIX[category] || 'IT';
  const brandPart = (brand || title).toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, 10) || 'ITEM';
  const sizePart = (size || '').toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, 8);
  return [prefix, brandPart, sizePart].filter(Boolean).join('-');
};

const mapUpcItemToLookupResult = (scannedUpc: string, item: any): UpcLookupResult | null => {
  if (!item?.title) return null;

  const { category, alcoholType, subCategory } = categorizeUpcItem(item.category || '', item.title);

  return {
    sku: generateSku(category, item.brand || '', item.size || '', item.title),
    barcode: item.upc || item.ean || scannedUpc,
    itemName: item.title,
    category,
    subCategory,
    brand: item.brand || undefined,
    size: item.size || undefined,
    alcoholType,
  };
};
